import jsPDF from 'jspdf';

const PX_PER_INCH = 96;
const MM_PER_INCH = 25.4;
const pxToMm = (px) => (px / PX_PER_INCH) * MM_PER_INCH;
const hasNonLatin1 = (str) => {
  const s = String(str || '');
  for (let i = 0; i < s.length; i += 1) {
    const code = s.codePointAt(i);
    if (code > 0xff) return true;
    if (code > 0xffff) i += 1;
  }
  return false;
};

function createTextImage(text, { fontSize = 8, bold = false, color = 'rgb(51,51,51)' } = {}) {
  const dpr = 2;
  const padX = 2;
  const padTop = 2;
  const font = `${
    bold ? '700' : '400'
  } ${fontSize}px system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif`;
  const measureCanvas = document.createElement('canvas');
  const measureCtx = measureCanvas.getContext('2d');
  measureCtx.font = font;
  const textMetrics = measureCtx.measureText(String(text || ''));
  const widthPx = Math.ceil(textMetrics.width + padX * 2);
  const heightPx = Math.ceil(fontSize + padTop * 2);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(2, Math.floor(widthPx * dpr));
  canvas.height = Math.max(2, Math.floor(heightPx * dpr));
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(String(text || ''), padX, padTop + fontSize);
  const dataUrl = canvas.toDataURL('image/png');
  return {
    dataUrl,
    widthMm: pxToMm(widthPx),
    heightMm: pxToMm(heightPx),
    topPadPx: padTop,
    fontSize,
  };
}

export async function exportQrCodePdf({
  qrDataUrl,
  fileName = 'certificate_qr_code.pdf',
  qrSize = 80,
  title = '',
  size = '',
  medium = '',
  year = '',
  edition = '',
  artist_name = '',
  artistName = '',
}) {
  const pdf = new jsPDF({
    orientation: 'p',
    unit: 'mm',
    format: 'a4',
  });

  const pageH = pdf.internal.pageSize.getHeight();

  const cardW = 105;
  const cardH = 50;
  const cardX = 5.3;
  const cardY = pageH / 8;

  pdf.setFillColor(255, 255, 255);
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.3);
  pdf.rect(cardX, cardY, cardW, cardH, 'FD');

  const leftW = cardW / 2;
  const leftX = cardX;
  const rightX = cardX + leftW;
  const colPad = 2;
  const sepX = rightX;

  const bottomOffset = 0;
  const gapToCaption = 2;
  const boxPad = 4;
  const widthAvail = leftW - colPad * 2;
  const bottomBaselineY = cardY + cardH - colPad - bottomOffset;
  const bottomYForQr = bottomBaselineY - gapToCaption;
  const cInset = 2;
  const alignTopY = cardY + colPad + 4;
  const deltaTop = 2.5;
  const topYForQr = alignTopY - cInset - deltaTop;
  const heightAvail = Math.max(0, bottomYForQr - topYForQr);
  const maxImgSize = Math.max(
    0,
    Math.min(qrSize, widthAvail - boxPad * 2, heightAvail - boxPad * 2),
  );
  const boxSize = maxImgSize + boxPad * 2;
  const boxX = leftX + (leftW - boxSize) / 2;
  const boxY = topYForQr;

  pdf.setDrawColor(255, 255, 255);
  pdf.setLineWidth(0.001);
  pdf.rect(boxX, boxY, boxSize, boxSize);

  if (qrDataUrl) {
    const imgX = boxX + boxPad;
    const imgY = boxY + boxPad;
    pdf.addImage(qrDataUrl, 'PNG', imgX, imgY, maxImgSize, maxImgSize);
  }

  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.4);
  pdf.line(sepX, cardY, sepX, cardY + cardH);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  const titleLabelWidth = pdf.getTextWidth('Title:');
  const cornerScale = 0.4;
  const m = Math.min(titleLabelWidth * cornerScale, boxSize - 2 * cInset);
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.3);
  pdf.line(boxX + cInset, boxY + cInset, boxX + cInset + m, boxY + cInset);
  pdf.line(boxX + cInset, boxY + cInset, boxX + cInset, boxY + cInset + m);
  pdf.line(boxX + boxSize - cInset - m, boxY + cInset, boxX + boxSize - cInset, boxY + cInset);
  pdf.line(boxX + boxSize - cInset, boxY + cInset, boxX + boxSize - cInset, boxY + cInset + m);
  pdf.line(boxX + cInset, boxY + boxSize - cInset, boxX + cInset + m, boxY + boxSize - cInset);
  pdf.line(boxX + cInset, boxY + boxSize - cInset - m, boxX + cInset, boxY + boxSize - cInset);
  pdf.line(
    boxX + boxSize - cInset - m,
    boxY + boxSize - cInset,
    boxX + boxSize - cInset,
    boxY + boxSize - cInset,
  );
  pdf.line(
    boxX + boxSize - cInset,
    boxY + boxSize - cInset - m,
    boxX + boxSize - cInset,
    boxY + boxSize - cInset,
  );

  pdf.setTextColor(51, 51, 51);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7);
  const captionX = leftX + leftW / 2;
  const captionY = bottomBaselineY;
  pdf.text('QR for Physical Artwork', captionX, captionY, { align: 'center' });

  let curX = rightX + colPad;
  const labelColor = [51, 51, 51];
  const valueColor = [51, 51, 51];
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(6);
  const labelCandidates = [
    'Title of artwork:',
    'Size:',
    'Medium:',
    'Year completed:',
    'Edition #:',
  ];
  const labelW = Math.max(...labelCandidates.map((l) => pdf.getTextWidth(l))) + 2.5;

  const rows = [
    { label: 'Title of artwork:', value: title, bold: true },
    { label: 'Size:', value: size, bold: false },
    { label: 'Medium:', value: medium, bold: false },
    { label: 'Year completed:', value: year, bold: false },
    { label: 'Edition #:', value: edition, bold: true },
  ];

  const divY = cardY + cardH / 2;
  const topStartY = cardY + colPad + 3;
  const topEndY = divY - 3;
  const step = rows.length > 1 ? (topEndY - topStartY) / (rows.length - 1) : 0;
  const curXTop = curX + 2;
  rows.forEach((r, idx) => {
    const y = rows.length > 1 ? topStartY + step * idx : (topStartY + topEndY) / 2;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(6);
    pdf.setTextColor(...labelColor);
    pdf.text(r.label, curXTop, y);
    pdf.setFont('helvetica', r.bold ? 'bold' : 'normal');
    pdf.setFontSize(r.bold ? 8 : 7);
    const vc2 = valueColor;
    pdf.setTextColor(...vc2);
    const valueStr = String(r.value || '');
    if (hasNonLatin1(valueStr)) {
      const fontSize = r.bold ? 8 : 7;
      const { dataUrl, widthMm, heightMm } = createTextImage(valueStr, {
        fontSize,
        bold: r.bold,
        color: 'rgb(51,51,51)',
      });
      const imgX = curXTop + labelW;
      const imgY = y - pxToMm(fontSize + 2);
      pdf.addImage(dataUrl, 'PNG', imgX, imgY, widthMm, heightMm);
    } else {
      pdf.text(valueStr, curXTop + labelW, y);
    }
  });

  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.4);
  pdf.line(sepX, divY, cardX + cardW, divY);
  const artist = artist_name || artistName || '';
  const bottomRoleY = bottomBaselineY;
  const bottomArtistY = bottomRoleY - 5;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.setTextColor(51, 51, 51);
  const artistStr = String(artist || '');
  if (hasNonLatin1(artistStr)) {
    const { dataUrl, widthMm, heightMm } = createTextImage(artistStr, {
      fontSize: 10,
      bold: true,
      color: 'rgb(51,51,51)',
    });
    const imgY = bottomArtistY - pxToMm(10 + 2);
    pdf.addImage(dataUrl, 'PNG', curXTop, imgY, widthMm, heightMm);
  } else {
    pdf.text(artistStr, curXTop, bottomArtistY);
  }
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(6);
  pdf.setTextColor(...labelColor);
  pdf.text('Artist', curXTop, bottomRoleY);

  pdf.save(fileName);
}
